import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useState
} from 'react'
import { useReactToPrint } from 'react-to-print'
import { useTranslation } from 'react-i18next'
import TextField from '@/shared/components/TextField/TextField'
import styles from './PrintGridForm.module.scss'
import { usePrintGridStore } from '@/features/table-cards/store/usePrintGridStore'
import Button from '@/shared/components/Button/Button'
import Alert from '@/shared/components/Alert/Alert'

interface PrintGridFormProps {
  printRef: MutableRefObject<HTMLDivElement | null>
  setSelectedRowIds: Dispatch<SetStateAction<string[]>>
  setSelectedTableCards: Dispatch<SetStateAction<string[]>>
  closeModal: () => void
}

function PrintGridForm(props: PrintGridFormProps) {
  const { printRef, setSelectedRowIds, setSelectedTableCards, closeModal } =
    props
  const { t } = useTranslation()

  const [showFormSuccess, setShowFormSuccess] = useState(false)
  const [verticalGap, setVerticalGap] = useState(0)
  const [horizontalGap, setHorizontalGap] = useState(0)
  const [cardsPerPage, setCardsPerPage] = useState(1)
  const [printGridButtonDisabled, setPrintGridButtonDisabled] = useState(false)

  //   const [isInLandScapeMode, setIsInLandScapeMode] = useState<
  //     undefined | boolean
  //   >(undefined)

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onAfterPrint: () => {
      setSelectedRowIds([])
      setSelectedTableCards([])
      usePrintGridStore.getState().reset()
      closeModal()
    }
  })

  useEffect(() => {
    usePrintGridStore.getState().setGap({ horizontal: 0, vertical: 0 })
    usePrintGridStore.getState().setTableCardsPerPage(1)
  }, [])

  const onApplyClick = () => {
    usePrintGridStore
      .getState()
      .setGap({ horizontal: horizontalGap, vertical: verticalGap })
    usePrintGridStore.getState().setTableCardsPerPage(cardsPerPage)

    setShowFormSuccess(true)
    setPrintGridButtonDisabled(false)
  }

  const handleOnPrintClick = () => {
    handlePrint()
  }

  return (
    <div className={styles.printGridFormWrapper}>
      {showFormSuccess && (
        <Alert
          severity="success"
          message={t('PRINT_GRID.SUCCESS_MESSAGE')}
          onClose={() => setShowFormSuccess(false)}
        />
      )}
      <div className={`${styles.printGridFormDesciption} body-medium`}>
        {t('PRINT_GRID.DESCRIPTION')}
      </div>
      <div className={styles.printGridFormRow}>
        <TextField
          type="number"
          label={t('PRINT_GRID.HORIZONTAL_GAP')}
          focused
          value={String(horizontalGap)}
          onChange={e => {
            setHorizontalGap(Number(e.target.value))
            setPrintGridButtonDisabled(true)
            setShowFormSuccess(false)
          }}
          disabled={cardsPerPage === 1}
          onlyInteger
          defaultValue="0"
        />
        <TextField
          type="number"
          label={t('PRINT_GRID.VERTICAL_GAP')}
          focused
          value={String(verticalGap)}
          onChange={e => {
            setVerticalGap(Number(e.target.value))
            setPrintGridButtonDisabled(true)
            setShowFormSuccess(false)
          }}
          disabled={cardsPerPage === 1}
          onlyInteger
          defaultValue="0"
        />
      </div>
      <div className={styles.printGridFormRow}>
        <TextField
          type="number"
          label={t('PRINT_GRID.CARDS_PER_PAGE')}
          focused
          value={String(cardsPerPage)}
          onChange={e => {
            setCardsPerPage(Number(e.target.value))
            setPrintGridButtonDisabled(true)
            setShowFormSuccess(false)
          }}
          onlyInteger
          defaultValue="1"
        />
      </div>
      {/* TODO: enable rotation option */}
      {/* {showRotateTableCardButton && (
        <div className={styles.printGridFormRow}>
          <IconButton
            icon="las la-sync icon-big"
            onClick={() => setIsInLandScapeMode(!isInLandScapeMode)}
            color="primary"
            rotate={isInLandScapeMode ? 90 : 0}
          />
          <p className={`${styles.printGridFormRotationText} body-medium`}>
            {isInLandScapeMode
              ? 'Table cards will be printed in landscape orientation.'
              : 'Table cards will be printed in portrait orientation.'}
          </p>
        </div>
      )} */}
      <div className={styles.printGridFormButtonContainer}>
        <Button
          onClick={handleOnPrintClick}
          label={t('PRINT_GRID.PRINT')}
          variant="contained"
          color="primary"
          disabled={printGridButtonDisabled}
        />
        <Button
          onClick={onApplyClick}
          label={t('PRINT_GRID.APPLY_SETTINGS')}
          variant="contained"
          color="primary"
        />
      </div>
    </div>
  )
}

export default PrintGridForm
